/**
 * `<page-subject-binding>` — the subject-binding editor, routed at
 * `/instances/:id/subject-bindings/new` and `…/subject-bindings/:name`.
 *
 * It is the same frame as the generic policy editor — left column authored, right
 * column derived — for one kind, and it exists separately because this is where
 * **instance API keys are minted** (`SC-F521`). A key lifecycle behind a kind picker
 * would be the wrong shape for the thing an operator is actually doing.
 *
 * Implements `SC-F520`, `SC-F521` and `SC-F522`, with the envelope from `SC-F502`.
 * `SubjectBinding` uses the *inert* envelope: `priority` decodes and orders nothing
 * for this kind, so it is not offered, and the kind carries no `enforcementMode` at
 * all — there is no shadow rollout for an identity mapping.
 *
 * Two behaviours worth stating.
 *
 * **A minted key makes this page's copy of the object stale.** The mint writes
 * `apiKeys[].hashRef` server-side, so the form asks for a refetch rather than
 * inventing a local row: a row with no `hashRef` is indistinguishable from the
 * silent-partial-outage state that Sluice's skip-with-a-warning behaviour produces.
 *
 * **A save sends `If-Match`.** There is one draft per instance and every maintainer
 * edits the same object set, so last-write-wins would silently discard a colleague's
 * edit. The tag is the object's content digest.
 *
 * Stories: `SC-S518`, `SC-S519`, `SC-S502`.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import { toast, type BnTab, type BnTabsChangeDetail } from "../components/index.js";

import { ApiError, parseError, validationFields } from "../api/errors.js";
import {
  createPolicy,
  deletePolicy,
  getPolicyWithETag,
  getPolicyYaml,
  listPolicies,
  replacePolicy,
  type ObjectMeta,
  type PolicyFinding,
  type PolicyObject,
} from "../api/policies.js";
import { hrefFor, navigate } from "../nav.js";

import "./policy/editor-subjectbinding.js";
import type { SubjectBindingSpec } from "./policy/editor-subjectbinding.js";
import type { SpecChangeDetail } from "./policy/editor-sqlaccess.js";
import "./policy/policy-envelope.js";
import type { EnvelopeChangeDetail } from "./policy/policy-envelope.js";
import "./policy/policy-findings.js";
import { countFindings } from "./policy/policy-findings.js";
import { loadPolicySchema, type PolicySchema } from "./policy/schema.js";

/** The right-hand pane's tabs. */
const TABS: BnTab[] = [
  { id: "yaml", label: "YAML" },
  { id: "findings", label: "Findings" },
];

@customElement("page-subject-binding")
export class PageSubjectBinding extends LitElement {
  @property() instanceId = "";
  @property({ type: Boolean }) create = false;
  @property() name = "";

  @state() private schema: PolicySchema | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;

  /** The stored record, or null while creating. */
  @state() private object: PolicyObject | null = null;
  @state() private etag = "";

  @state() private metadata: ObjectMeta = { name: "" };
  @state() private spec: SubjectBindingSpec = {};
  @state() private findings: PolicyFinding[] = [];
  @state() private dirty = false;
  @state() private saving = false;
  @state() private saveError: ApiError | null = null;

  /** The rendered manifest of what is **stored**, not of what is on screen: there is
   * no client-side renderer and there must not be one, because the bundle digest is a
   * function of the server's bytes. */
  @state() private yaml = "";
  @state() private yamlStale = false;
  @state() private tab = "yaml";

  @state() private confirmingDelete = false;
  @state() private deleting = false;

  /** Same-kind siblings, for the envelope's ordering preview. */
  @state() private peerRows: Array<{ name: string; priority: number }> = [];

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 22rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
      overflow-wrap: anywhere;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 70ch;
    }
    .actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .columns {
      display: grid;
      grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
      gap: var(--bino-space-lg);
      align-items: start;
    }
    @media (max-width: 68rem) {
      .columns {
        grid-template-columns: minmax(0, 1fr);
      }
    }
    .left,
    .right {
      display: grid;
      gap: var(--bino-space-lg);
    }
    .code {
      display: block;
      height: 26rem;
    }
    .code > bn-code {
      display: block;
      height: 100%;
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.boot();
  }

  private async boot() {
    this.loading = true;
    try {
      const { schema } = await loadPolicySchema();
      this.schema = schema;
      if (this.create) {
        this.metadata = { name: "" };
        this.spec = {};
        this.findings = [];
        this.error = null;
      } else {
        await this.reload();
      }
    } catch (err) {
      this.error = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /** Refetch the stored object. Called on load, after every save, and whenever the
   * editor reports that a mint or a revoke changed the record server-side. */
  private async reload() {
    const { value, etag } = await getPolicyWithETag(this.instanceId, "SubjectBinding", this.name);
    this.object = value;
    this.etag = etag || value.digest;
    this.metadata = value.metadata;
    this.spec = (value.spec ?? {}) as SubjectBindingSpec;
    this.findings = value.findings ?? [];
    this.dirty = false;
    this.error = null;
    void this.loadYaml();
  }

  /** The YAML pane. A failure here is not the page's error state: the form is already
   * usable and the pane says it could not render. */
  private async loadYaml() {
    if (this.create || this.name === "") return;
    try {
      this.yaml = await getPolicyYaml(this.instanceId, "SubjectBinding", this.name);
      this.yamlStale = false;
    } catch (err) {
      const e = parseError(err);
      this.yaml = `# The rendered manifest could not be read.\n# ${e.code}: ${e.message}`;
      this.yamlStale = false;
    }
  }

  /** Same-kind siblings for the envelope's ordering preview. Priority orders nothing
   * for this kind, so the list is only ever used to show the name-sorted neighbourhood
   * — and losing it costs that, not the page. */
  private async peers(): Promise<Array<{ name: string; priority: number }>> {
    try {
      const page = await listPolicies(this.instanceId, { kind: "SubjectBinding", limit: 200 });
      return page.items.map((r) => ({ name: r.name, priority: r.priority }));
    } catch {
      return [];
    }
  }

  override firstUpdated() {
    void this.peers().then((rows) => (this.peerRows = rows));
  }

  /* ── Editing ───────────────────────────────────────────────────────── */

  private onEnvelope(e: CustomEvent<EnvelopeChangeDetail>) {
    e.stopPropagation();
    this.metadata = e.detail.metadata;
    this.dirty = true;
  }

  private onSpec(e: CustomEvent<SpecChangeDetail<SubjectBindingSpec>>) {
    e.stopPropagation();
    this.spec = e.detail.spec;
    this.dirty = true;
    // The pane still shows the stored render, and it now differs from the form.
    this.yamlStale = true;
  }

  /**
   * Save the whole document.
   *
   * There is no partial write for a spec: policy specs are nested discriminated
   * unions, a partial merge has no defensible semantics, and this form holds the whole
   * object anyway. `If-Match` carries the stored digest so a colleague's edit is a
   * `412` rather than a silent overwrite.
   */
  private async save() {
    this.saving = true;
    this.saveError = null;
    try {
      const input = {
        kind: "SubjectBinding" as const,
        metadata: this.metadata,
        spec: this.spec as Record<string, unknown>,
      };
      if (this.create) {
        const created = await createPolicy(this.instanceId, input);
        toast(`Binding ${created.metadata.name} created.`, "success");
        navigate(
          hrefFor("instances", this.instanceId, "subject-bindings", created.metadata.name),
        );
        return;
      }
      await replacePolicy(this.instanceId, "SubjectBinding", this.name, input, {
        ifMatch: this.etag,
      });
      toast("Binding saved to the draft.", "success");
      await this.reload();
    } catch (err) {
      this.saveError = parseError(err);
    } finally {
      this.saving = false;
    }
  }

  private async deleteObject() {
    this.deleting = true;
    try {
      await deletePolicy(this.instanceId, "SubjectBinding", this.name);
      toast(`Binding ${this.name} removed from the draft.`, "success");
      navigate(hrefFor("instances", this.instanceId, "subject-bindings"));
    } catch (err) {
      const e = parseError(err);
      this.saveError = e;
      this.confirmingDelete = false;
    } finally {
      this.deleting = false;
    }
  }

  /* ── Rendering ─────────────────────────────────────────────────────── */

  private renderError(err: ApiError, heading: string) {
    return html`<bn-card heading=${heading}>
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ]}
        ></bn-kv>
        ${validationFields(err).length > 0
          ? html`<bn-kv
              .rows=${validationFields(err).map((f) => ({ label: f.path, value: f.reason }))}
            ></bn-kv>`
          : nothing}
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.boot()}>Try again</bn-button>
          <bn-button
            @click=${() => navigate(hrefFor("instances", this.instanceId, "subject-bindings"))}
          >
            Back to bindings
          </bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  private renderRight() {
    const counts = countFindings(this.findings);
    const tabs: BnTab[] = TABS.map((t) =>
      t.id === "findings" && counts.errors + counts.blocking + counts.warnings > 0
        ? { ...t, label: `Findings (${counts.errors + counts.blocking + counts.warnings})` }
        : t,
    );
    return html`<bn-card>
      <bn-tabs
        label="Derived views"
        .tabs=${tabs}
        .active=${this.tab}
        @change=${(e: CustomEvent<BnTabsChangeDetail>) => (this.tab = e.detail.id)}
      >
        <div slot="yaml">
          ${this.create
            ? html`<p class="meta">
                The manifest is rendered from what is <strong>stored</strong>. Save the binding to see
                it.
              </p>`
            : html`
                <p class="meta">
                  ${this.yamlStale
                    ? "This is the manifest of the stored object. It does not yet include the edits above."
                    : "The manifest of the stored object, rendered by the server."}
                </p>
                <div class="code">
                  <bn-code
                    language="yaml"
                    label=${`Rendered SubjectBinding ${this.name}`}
                    filename=${`policies.d/10-subjectbinding-${this.name}.yaml`}
                    .value=${this.yaml}
                  ></bn-code>
                </div>
              `}
        </div>
        <div slot="findings">
          <policy-findings
            .findings=${this.findings}
            emptyText="No findings. The linter runs on every save, so this is the answer for the stored object."
          ></policy-findings>
        </div>
      </bn-tabs>
    </bn-card>`;
  }

  override render() {
    if (this.loading) return html`<bn-spinner block label="Loading the binding"></bn-spinner>`;
    if (this.error) return this.renderError(this.error, "Could not open this binding");
    const schema = this.schema;
    if (!schema) return html`<bn-spinner block label="Loading the form schema"></bn-spinner>`;

    return html`
      <header>
        <div class="head-text">
          <h1>${this.create ? "New subject binding" : this.name}</h1>
          <p class="lede">
            ${schema.kind("SubjectBinding")?.help ?? ""}
          </p>
        </div>
        <div class="actions">
          ${this.create
            ? nothing
            : html`<bn-button
                variant="danger"
                @click=${() => (this.confirmingDelete = true)}
              >
                Delete
              </bn-button>`}
          <bn-button
            variant="primary"
            ?loading=${this.saving}
            ?disabled=${!this.dirty && !this.create}
            @click=${() => void this.save()}
          >
            ${this.create ? "Create binding" : "Save to draft"}
          </bn-button>
        </div>
      </header>

      ${this.saveError ? this.renderError(this.saveError, "The binding was not saved") : nothing}

      <div class="columns">
        <div class="left">
          <bn-card heading="Identity">
            <policy-envelope
              .schema=${schema}
              kind="SubjectBinding"
              .metadata=${this.metadata}
              ?create=${this.create}
              .peers=${this.peerRows}
              .findings=${this.findings}
              .enabled=${this.object?.enabled ?? true}
              @envelope-change=${this.onEnvelope}
            ></policy-envelope>
          </bn-card>

          <bn-card heading="The binding">
            <policy-editor-subjectbinding
              .schema=${schema}
              .spec=${this.spec}
              .findings=${this.findings}
              .instanceId=${this.instanceId}
              .objectName=${this.name}
              ?saved=${!this.create}
              @spec-change=${this.onSpec}
              @policy-object-stale=${() => void this.reload()}
            ></policy-editor-subjectbinding>
          </bn-card>
        </div>
        <div class="right">${this.renderRight()}</div>
      </div>

      <bn-modal
        ?open=${this.confirmingDelete}
        tone="destructive"
        heading=${`Delete ${this.name}?`}
        @close=${() => (this.confirmingDelete = false)}
      >
        <p>
          The binding is removed from this instance's draft. It keeps serving until the draft is
          published.
        </p>
        <p slot="consequence">
          Every subject this binding resolved stops being recognised once the change is published.
          Policies that name those subjects will match nobody, which makes them broader or narrower
          depending on whether they allow or deny — check the policies that reference this binding's
          groups or key ids before publishing.
        </p>
        <bn-button slot="footer" @click=${() => (this.confirmingDelete = false)}>Cancel</bn-button>
        <bn-button
          slot="footer"
          variant="danger"
          ?loading=${this.deleting}
          @click=${() => void this.deleteObject()}
        >
          Delete from the draft
        </bn-button>
      </bn-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-subject-binding": PageSubjectBinding;
  }
}
